import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  onEnterKeyPress,
  scrollToTopOfPage,
  FactCard,
  AppIcon,
  CareerPath,
  CareerCategory,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CareerQuestion,
  CareerUserResponseAnswer,
  CareerUserResponseSet,
  SCHOOL_LEVEL,
  SchoolLevel,
} from '@backrs_ui/grow-ui/src/models/models';

import { Box, SxProps, Theme, Button, Typography, useMediaQuery, Drawer } from '@mui/material';

import { AccountFieldsQuestion } from './molecules/AccountFieldsQuestion';
import { FinalQuestionModal } from './molecules/FinalQuestionModal';
import { Question } from './molecules/Question';
import {
  determineIfNextIsDisabled,
  getNextQuestionIndex,
  schoolLevelQuestionProps,
  determineIfQuestionIsSkippable,
  isZipValid,
  SurveyStep,
} from './survey.util';
import { useAgentContext } from './SurveyPageContainer.agentContext';
import { SurveyPageHeader } from './SurveyPageHeader';

const BUTTON_STRINGS = appStrings.common.button;
const STRINGS = appStrings.modules.survey;

type SurveyPageProps = Readonly<{
  careerQuestions: CareerQuestion[];
  userResponseSet: CareerUserResponseSet;
  addUserQuestionResponse: (response: CareerUserResponseAnswer) => void;
  onFinishSurvey: (finalQuestionResponse?: CareerUserResponseAnswer) => void;
  onSelectSchoolLevel: (schoolLevel: SchoolLevel) => void;
  onConfirmSchoolLevel: (schoolLevel: SchoolLevel) => void;
  userHasBlueprint: boolean;
  careerPaths: CareerPath[];
  careerCategories: CareerCategory[];
  isGuest: boolean;
  onZipChange: (value: string) => void;
  onSchoolChange: (value: string) => void;
  onGraduationYearChange: (value: string) => void;
  onStartTermChange: (value: string) => void;
  onStartYearChange: (value: string) => void;
}>;

export const SurveyPage = ({
  careerQuestions,
  userResponseSet,
  addUserQuestionResponse,
  onFinishSurvey,
  onSelectSchoolLevel,
  onConfirmSchoolLevel,
  userHasBlueprint,
  careerPaths,
  careerCategories,
  isGuest,
  onZipChange,
  onSchoolChange,
  onGraduationYearChange,
  onStartTermChange,
  onStartYearChange,
}: SurveyPageProps) => {
  const location = useLocation();
  // e.g. "Go back" from the blueprint signup gate drops the visitor on the question they last answered
  const startAtLastQuestion =
    !!(location.state as { startAtLastQuestion?: boolean } | null)?.startAtLastQuestion &&
    !!userResponseSet.school_level;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(
    startAtLastQuestion ? careerQuestions.length - 1 : 0,
  );
  const isMobile = useMediaQuery('(max-width:600px)');
  const [isOpen, setIsDrawerOpen] = useState(false);
  const [step, setStep] = useState<SurveyStep>(startAtLastQuestion ? 'CAREER' : 'SCHOOL_LEVEL');

  const isOnSchoolLevelQuestion = step === 'SCHOOL_LEVEL';
  const isOnAccountFieldsQuestion = step === 'ACCOUNT_FIELDS';
  const isOnCareerQuestion = step === 'CAREER';

  useAgentContext(careerQuestions, userResponseSet, currentQuestionIndex, step);

  const navigate = useNavigate();

  const currentQuestionId = careerQuestions[currentQuestionIndex]?.id;

  const responsesForCurrentQuestion = userResponseSet.user_responses.find(
    (response) => response.career_question_id === currentQuestionId,
  );

  const handlePrevious = () => {
    if (isOnAccountFieldsQuestion) {
      setStep('SCHOOL_LEVEL');

      return;
    }

    if (isOnSchoolLevelQuestion) {
      // if we are on the school level question, we should go back to the intro page
      navigate(APP_ROUTE.SURVEY, { state: { startAtEnd: true } });

      return;
    }

    if (currentQuestionIndex === 0) {
      setStep(isGuest ? 'ACCOUNT_FIELDS' : 'SCHOOL_LEVEL');

      return;
    }

    const previousIndex = getNextQuestionIndex({
      index: currentQuestionIndex,
      direction: 'BACKWARD',
      userResponseSet,
      addUserQuestionResponse,
      careerQuestions: careerQuestions,
    });

    scrollToTopOfPage();

    setCurrentQuestionIndex(previousIndex);
  };

  const currentSchoolLevel = userResponseSet?.school_level;

  // non-students only provide a zip; students also need a graduation year and a start year,
  // plus a start term unless they're in high school (school is optional)
  const isStudentLevel = currentSchoolLevel !== 'no_current_enrollment';
  const isHighSchoolLevel = currentSchoolLevel === SCHOOL_LEVEL.HIGH_SCHOOL;
  const accountFieldsIncomplete =
    !isZipValid(userResponseSet.zip_code) ||
    (isStudentLevel &&
      (!userResponseSet.graduation_year ||
        (!isHighSchoolLevel && !userResponseSet.start_term) ||
        !userResponseSet.start_year));

  const isNextDisabled = isOnAccountFieldsQuestion
    ? accountFieldsIncomplete
    : determineIfNextIsDisabled({
        currentQuestionIndex,
        userResponseSet,
        careerQuestions,
        isOnSchoolLevelQuestion,
        currentSchoolLevel,
      });

  const isSkippable = determineIfQuestionIsSkippable({
    currentQuestionIndex,
    careerQuestions,
  });

  const handleNext = () => {
    if (isOnSchoolLevelQuestion) {
      if (!currentSchoolLevel) {
        return;
      }

      onConfirmSchoolLevel(currentSchoolLevel);
      // guest visitors collect zip + graduation year before the career questions
      setStep(isGuest ? 'ACCOUNT_FIELDS' : 'CAREER');

      return;
    }

    if (isOnAccountFieldsQuestion) {
      if (accountFieldsIncomplete) {
        return;
      }

      setStep('CAREER');

      return;
    }

    if (isNextDisabled) {
      return;
    }

    if (currentQuestionIndex === careerQuestions.length - 1) {
      onFinishSurvey();

      return;
    }

    scrollToTopOfPage();

    const nextIndex = getNextQuestionIndex({
      index: currentQuestionIndex,
      direction: 'FORWARD',
      userResponseSet,
      addUserQuestionResponse,
      careerQuestions: careerQuestions,
    });

    setCurrentQuestionIndex(nextIndex);
  };

  const handleFinalQuestionSaveAndContinue = (finalQuestionResponse: CareerUserResponseAnswer) => {
    // Save the final question response to Redux
    addUserQuestionResponse(finalQuestionResponse);

    // Call onFinishSurvey with the final question response data
    onFinishSurvey(finalQuestionResponse);
  };

  const currentQuestionFacts = careerQuestions[currentQuestionIndex]?.facts;
  const hasFacts = (currentQuestionFacts?.length ?? 0) > 0;
  const isFinalQuestion = isOnCareerQuestion && currentQuestionIndex === careerQuestions.length - 1;

  // "Start Your Blueprint" sits on the last step before the career questions
  const isLastPreCareerStep = isOnAccountFieldsQuestion || (isOnSchoolLevelQuestion && !isGuest);

  const nextButton = (
    <Button
      variant="contained"
      size="md"
      hierarchy="Primary"
      sx={nextButtonSx(isOnSchoolLevelQuestion)}
      onClick={handleNext}
      disabled={isNextDisabled}
      id="survey-next-button"
      data-ph-capture-attribute-name="survey-next-button"
    >
      {isLastPreCareerStep ? STRINGS.startYourBlueprint : BUTTON_STRINGS.next}
      <AppIcon
        iconName="ArrowRight"
        diameter="20px"
        color={isNextDisabled ? 'Colors/Foreground/fg-disabled' : 'Colors/Base/White'}
      />
    </Button>
  );

  const previousButton = (
    <Button
      variant="contained"
      size="md"
      hierarchy="Secondary gray"
      sx={buttonSx}
      onClick={handlePrevious}
      id="survey-previous-button"
      data-ph-capture-attribute-name="survey-previous-button"
    >
      {BUTTON_STRINGS.previous}
    </Button>
  );

  const renderActiveQuestion = () => {
    if (isFinalQuestion) {
      return (
        <FinalQuestionModal
          question={careerQuestions[currentQuestionIndex]}
          responsesForQuestion={responsesForCurrentQuestion}
          userResponseSet={userResponseSet}
          onBack={handlePrevious}
          onSaveAndContinue={handleFinalQuestionSaveAndContinue}
        />
      );
    }

    switch (step) {
      case 'SCHOOL_LEVEL':
        return (
          <Question
            {...schoolLevelQuestionProps({
              userResponseSet,
              currentSchoolLevel,
              handleSelectSchoolLevel: onSelectSchoolLevel,
              careerQuestions,
            })}
            isSchoolLevelQuestion={true}
            actionButton={nextButton}
            cardWidth="600px"
          />
        );
      case 'ACCOUNT_FIELDS':
        return (
          <AccountFieldsQuestion
            userResponseSet={userResponseSet}
            isStudent={isStudentLevel}
            isHighSchool={isHighSchoolLevel}
            onZipChange={onZipChange}
            onSchoolChange={onSchoolChange}
            onGraduationYearChange={onGraduationYearChange}
            onStartTermChange={onStartTermChange}
            onStartYearChange={onStartYearChange}
            previousButton={previousButton}
            nextButton={nextButton}
          />
        );
      case 'CAREER':
        return (
          <Question
            question={careerQuestions[currentQuestionIndex]}
            responsesForQuestion={responsesForCurrentQuestion}
            userResponseSet={userResponseSet}
            careerQuestions={careerQuestions}
            addUserQuestionResponse={addUserQuestionResponse}
            handleFactClick={() => setIsDrawerOpen(true)}
            isSkippable={isSkippable}
            cardWidth={hasFacts ? '480px' : '700px'}
          />
        );
    }
  };

  const activeQuestionView = renderActiveQuestion();

  const showFactsView = isOnCareerQuestion && hasFacts;

  const activeQuestionFactView = showFactsView && !isMobile && (
    <Box sx={factCardTransitionSx}>
      <FactCard facts={currentQuestionFacts || []} />
    </Box>
  );

  useEffect(() => {
    setTimeout(() => {
      // open fact popup on every career question change
      if (isOnCareerQuestion && showFactsView && isMobile) {
        setIsDrawerOpen(true);
      }
    }, 200);
  }, [currentQuestionIndex, isOnCareerQuestion, showFactsView, isMobile]);

  return (
    <Box sx={containerStyle} tabIndex={0} onKeyDown={(event) => onEnterKeyPress(event, handleNext)}>
      {isFinalQuestion ? (
        activeQuestionView
      ) : (
        <>
          <Box sx={headerContainerSx}>
            <SurveyPageHeader
              userHasBlueprint={userHasBlueprint}
              careerPaths={careerPaths}
              careerCategories={careerCategories}
            />
          </Box>

          <Box sx={questionAndActionContainerStyle}>
            <Box sx={questionViewContainerSx}>
              {activeQuestionView}
              {activeQuestionFactView}
            </Box>
          </Box>

          {isOnCareerQuestion && (
            <Box sx={buttonContainerStyle}>
              {previousButton}
              <Typography variant="text-sm-medium" color="Colors/Foreground/fg-secondary">
                {STRINGS.pageCount(currentQuestionIndex + 1, careerQuestions.length)}
              </Typography>
              {nextButton}
            </Box>
          )}

          <Drawer
            anchor="bottom"
            open={isOpen}
            onClose={() => setIsDrawerOpen(false)}
            PaperProps={{ sx: drawerSx }}
          >
            <FactCard facts={currentQuestionFacts || []} />
          </Drawer>
        </>
      )}
    </Box>
  );
};

const containerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-4xl'],
  flexDirection: 'column',
  minHeight: '100%',
  width: '100%',
  containerType: 'inline-size',
  '@media (max-width: 600px)': {
    gap: 0,
  },
  paddingBottom: backrs.spacing['spacing-2xl'],
  paddingInline: backrs.spacing['spacing-xs'],
});

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  '@media (max-width: 600px)': {
    px: backrs.spacing['spacing-xl'],
  },
});

const questionAndActionContainerStyle: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
};

const buttonContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  zIndex: 10,
  pt: backrs.spacing['spacing-4xl'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  '@media (max-width: 600px)': {
    gap: backrs.spacing['spacing-lg'],
    borderTop: 'none',
    px: backrs.spacing['spacing-xl'],
    mb: backrs.spacing['spacing-4xl'],
  },
  '@media (max-width: 600px) and (max-height: 800px)': {
    mb: 0,
  },
});

const factCardTransitionSx: SxProps<Theme> = {
  animation: 'fadeInUp 0.4s ease-out 0.1s both',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  maxWidth: '244px',
  width: '244px',
  flexShrink: 1,
  minWidth: 0,
};

const questionViewContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-4xl'],
  justifyContent: 'center',
  alignItems: 'stretch',
  width: '100%',
  minWidth: 0,
  '@media (max-width: 600px)': {
    gap: 0,
  },
});

const buttonSx: SxProps<Theme> = {
  minWidth: 'fit-content',
  '@media (max-width: 600px)': {
    '&:hover': {
      boxShadow: 'none',
      transform: 'none',
    },
  },
};

const nextButtonSx =
  (isFirstQuestion: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    ...buttonSx,
    '@media (max-width: 600px)': {
      '&:hover': {
        boxShadow: 'none',
        transform: 'none',
        backgroundColor: backrs.colors['Colors/Background/bg-disabled'],
        color: backrs.colors['Colors/Foreground/fg-disabled'],
      },
    },
    WebkitTapHighlightColor: 'transparent',
    width: isFirstQuestion ? '90%' : 'fit-content',
  });

const drawerSx: SxProps<Theme> = ({ backrs }) => ({
  borderTopRightRadius: backrs.radius['radius-xl'],
  borderTopLeftRadius: backrs.radius['radius-xl'],
});
